import { motion } from 'framer-motion';
import { format, isToday } from 'date-fns';
import styles from './Header.module.css';

interface HeaderProps {
  date: Date;
  completedCount: number;
  totalCount: number;
  mitsCompleted?: number;
  mitsTotal?: number;
  deepWorkMinutes?: number;
  shallowWorkMinutes?: number;
  missedMinutes?: number;
  onShutdownClick?: () => void;
  onSettingsClick?: () => void;
  onTasksClick?: () => void;
  onAddTaskClick?: () => void;
  onTodayClick?: () => void;
}

export function Header({
  date,
  completedCount,
  totalCount,
  mitsCompleted = 0,
  mitsTotal = 0,
  deepWorkMinutes = 0,
  shallowWorkMinutes = 0,
  missedMinutes = 0,
  onShutdownClick,
  onTasksClick,
  onAddTaskClick,
  onSettingsClick,
  onTodayClick,
}: HeaderProps) {
  const formattedDate = format(date, 'EEEE, MMM d, yyyy');
  const showTodayButton = !isToday(date) && onTodayClick;

  // Format hours for compact display
  const formatHoursCompact = (minutes: number): string => {
    if (minutes === 0) return '0h';
    const hours = minutes / 60;
    if (hours < 1) return `${Math.round(minutes)}m`;
    return `${hours.toFixed(1)}h`;
  };

  const deepWorkDisplay = formatHoursCompact(deepWorkMinutes);
  const missedDisplay = formatHoursCompact(missedMinutes);
  const totalMinutes = deepWorkMinutes + shallowWorkMinutes;
  const hasMetrics = totalMinutes > 0;
  const hasMissed = missedMinutes > 0;

  return (
    <header className={styles.header}>
      <div className={styles.dateSection}>
        <motion.h1
          className={styles.date}
          key={formattedDate}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {formattedDate}
        </motion.h1>
        {showTodayButton && (
          <motion.button
            className={styles.todayBtn}
            onClick={onTodayClick}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Go to today"
          >
            Today
          </motion.button>
        )}
      </div>

      <div className={styles.actions}>
        {/* Compact Deep Work Metrics */}
        {hasMetrics && (
          <motion.div
            className={styles.metricsCompact}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            title={`Deep work: ${formatHoursCompact(deepWorkMinutes)} | Shallow: ${formatHoursCompact(shallowWorkMinutes)}`}
            aria-label={`${deepWorkDisplay} deep work logged today`}
          >
            <span className={styles.metricsIcon} aria-hidden="true">
              ●
            </span>
            <span className={styles.metricsValue}>{deepWorkDisplay}</span>
            <span className={styles.metricsLabel}>deep</span>
          </motion.div>
        )}

        {/* Missed Time - only show if there are missed blocks */}
        {hasMissed && (
          <motion.div
            className={styles.missedCompact}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.12 }}
            title={`Missed: ${missedDisplay} of planned time`}
            aria-label={`${missedDisplay} missed today`}
          >
            <span className={styles.missedIcon} aria-hidden="true">
              ○
            </span>
            <span className={styles.missedValue}>{missedDisplay}</span>
            <span className={styles.missedLabel}>missed</span>
          </motion.div>
        )}

        {/* MIT Progress - only show if there are MITs */}
        {mitsTotal > 0 && (
          <motion.div
            className={styles.mitProgress}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
            aria-label={`Most Important Tasks: ${mitsCompleted} of ${mitsTotal} completed`}
          >
            <span aria-hidden="true">★</span>
            <span>
              {mitsCompleted}/{mitsTotal}
            </span>
          </motion.div>
        )}

        {/* Tasks Button */}
        {onTasksClick && (
          <motion.button
            className={styles.iconBtn}
            onClick={onTasksClick}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Open tasks"
            title="Tasks"
          >
            <span aria-hidden="true">☑️</span>
          </motion.button>
        )}

        {/* Add Task Button */}
        {onAddTaskClick && (
          <motion.button
            className={styles.iconBtn}
            onClick={onAddTaskClick}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Add task"
            title="Add task"
          >
            <span aria-hidden="true">＋</span>
          </motion.button>
        )}

        {/* Shutdown Button - Always prominent */}
        <motion.button
          className={styles.shutdownBtn}
          onClick={onShutdownClick}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          aria-label="Start shutdown ritual"
        >
          <span aria-hidden="true">🌙</span>
          <span className={styles.shutdownLabel}>Shutdown</span>
        </motion.button>

        {/* Settings Button */}
        {onSettingsClick && (
          <motion.button
            className={styles.iconBtn}
            onClick={onSettingsClick}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Settings"
            title="Settings"
          >
            <span aria-hidden="true">⚙️</span>
          </motion.button>
        )}
      </div>
    </header>
  );
}
